'use client';

import { useState, useRef, useEffect, Suspense } from 'react';
import { useQueryParam } from '@/lib/routing';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Loader2, Download, Image as ImageIcon, Wand2, Upload, Link as LinkIcon, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { trpc } from '@/lib/trpc';
import FileUpload from '@/app/components/FileUpload';
import DownloadComponent from '@/app/components/DownloadComponent';
import PhotoModal from '@/app/components/ui/photo-modal';

const INPUT_METHOD_TABS = ['url', 'file', 'samples'] as const;
type InputMethodTabValue = typeof INPUT_METHOD_TABS[number];

function BackgroundRemovalPageContent() {
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [resultImageUrl, setResultImageUrl] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [originalFilename, setOriginalFilename] = useState('');
  // Single-param state for `tab` with type safety
  const [inputMethod, setInputMethod] = useQueryParam<InputMethodTabValue>('tab', {
    parse: (raw) => (raw ?? 'url') as InputMethodTabValue,
    serialize: (v) => v ?? null,
    defaultValue: 'url',
    replace: true, // keep history clean while switching tabs
  });
  const [sampleImages, setSampleImages] = useState<string[]>([]);
  const [isLoadingSamples, setIsLoadingSamples] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoModalImage, setPhotoModalImage] = useState('');
  const [photoModalTitle, setPhotoModalTitle] = useState('');

  // TRPC mutations
  const fromUrlMutation = trpc.backgroundRemoval.fromUrl.useMutation({
    onSuccess: (data: any) => {
      if (data.picsart && data.picsart.image_resource_url) {
        const proxyUrl = `/api/background-removal/proxy?url=${encodeURIComponent(data.picsart.image_resource_url)}`;
        setResultImageUrl(proxyUrl);
        setOriginalImageUrl(data.picsart.image_resource_url);
        
        // Generate filename from URL
        const urlFilename = imageUrl.split('/').pop()?.split('.')[0] || 'image';
        setOriginalFilename(urlFilename);
        
        toast.success('Background removed successfully!');
      } else {
        toast.error('Background removal failed - no image URL returned');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove background');
    },
  });

  const fromFileMutation = trpc.backgroundRemoval.fromFile.useMutation({
    onSuccess: (data: any) => {
      setResultImageUrl(data.imageUrl);
      setOriginalImageUrl(data.originalUrl || '');
      
      // Use the uploaded file's name
      if (uploadedFile) {
        const filenameWithoutExt = uploadedFile.name.split('.')[0];
        setOriginalFilename(filenameWithoutExt);
      }
      
      toast.success('Background removed successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove background');
    },
  });

  // Generate filename with format: originalname-bg-removed-eyn-date
  const generateFilename = (originalName: string) => {
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    return `${originalName}-bg-removed-eyn-${date}`;
  };

  // Open photo modal
  const openPhotoModal = (imageUrl: string, title: string) => {
    setPhotoModalImage(imageUrl);
    setPhotoModalTitle(title);
    setPhotoModalOpen(true);
  };

  // Fetch sample images from public folder
  const fetchSampleImages = async () => {
    setIsLoadingSamples(true);
    try {
      // Try to fetch a list of sample images from the public/samples folder
      const response = await fetch('/api/sample-images');
      if (response.ok) {
        const images = await response.json();
        setSampleImages(images);
      } else {
        // Fallback: use predefined sample images if API doesn't exist
        const fallbackImages = [
          '/samples/sample-1.jpg',
          '/samples/sample-2.jpg',
          '/samples/sample-3.jpg',
          '/samples/sample-4.jpg',
          '/samples/sample-5.jpg',
          '/samples/sample-6.jpg',
        ];
        setSampleImages(fallbackImages);
      }
    } catch (error) {
      console.error('Failed to fetch sample images:', error);
      // Use fallback images
      const fallbackImages = [
        '/samples/sample-1.jpg',
        '/samples/sample-2.jpg',
        '/samples/sample-3.jpg',
        '/samples/sample-4.jpg',
        '/samples/sample-5.jpg',
        '/samples/sample-6.jpg',
      ];
      setSampleImages(fallbackImages);
    } finally {
      setIsLoadingSamples(false);
    }
  };

  // Load sample images on component mount
  useEffect(() => {
    fetchSampleImages();
  }, []);

  // Handle sample image selection
  const handleSampleImageSelect = async (imagePath: string) => {
    try {
      // Fetch the sample image and convert to File object
      const response = await fetch(imagePath);
      const blob = await response.blob();
      
      // Extract filename from path
      const filename = imagePath.split('/').pop() || 'sample-image';
      
      // Create a File object from the blob
      const file = new File([blob], filename, { type: blob.type });
      
      // Set the file and switch to samples method
      setUploadedFile(file);
      setInputMethod('samples');
      setImageUrl(''); // Clear URL since we're using file
      setResultImageUrl('');
      setOriginalImageUrl('');
      setOriginalFilename('');
      
      toast.success('Sample image selected!');
    } catch (error) {
      console.error('Failed to load sample image:', error);
      toast.error('Failed to load sample image');
    }
  };

  const handleBackgroundRemoval = async () => {
    if (inputMethod === 'url') {
      if (!imageUrl.trim()) {
        toast.error('Please enter an image URL');
        return;
      }

      // URL validation for actual URLs
      try {
        new URL(imageUrl.trim());
      } catch {
        toast.error('Please enter a valid URL');
        return;
      }

      fromUrlMutation.mutate({ imageUrl: imageUrl.trim() });
    } else if (inputMethod === 'file' || inputMethod === 'samples') {
      if (!uploadedFile) {
        toast.error('Please upload an image file or select a sample image');
        return;
      }

      // Convert file to base64
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64Data = result.split(',')[1]; // Remove data:image/...;base64, prefix
        
        fromFileMutation.mutate({
          fileData: base64Data,
          fileName: uploadedFile.name,
          mimeType: uploadedFile.type
        });
      };
      reader.readAsDataURL(uploadedFile);
    }
  };


  const handleFileChange = (file: File | null) => {
    setUploadedFile(file);
    if (file) {
      setInputMethod('file');
    }
  };

  const clearInputs = () => {
    setImageUrl('');
    setUploadedFile(null);
    setResultImageUrl('');
    setOriginalImageUrl('');
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 animate-fade-in">
      <div className="container mx-auto flex justify-center">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">Background Removal Tool</h1>
            <p className="text-muted-foreground text-lg animate-slide-in-right">
              Remove backgrounds from images using AI-powered technology
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Input Section */}
            <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Input Image
            </CardTitle>
            <CardDescription>
              Choose how you want to provide your image
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Input Method Tabs */}
            <Tabs value={inputMethod} onValueChange={(value) => setInputMethod(value as InputMethodTabValue)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="url" className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  URL
                </TabsTrigger>
                <TabsTrigger value="file" className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Upload
                </TabsTrigger>
                <TabsTrigger value="samples" className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Samples
                </TabsTrigger>
              </TabsList>

              <TabsContent value="url" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="imageUrl">Image URL</Label>
                  <Input
                    id="imageUrl"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                  />
                </div>

                {imageUrl && (
                  <div className="mt-4">
                    <Label className="text-sm text-muted-foreground">Preview:</Label>
                    <div className="mt-2 rounded-lg overflow-hidden border cursor-pointer group relative" onClick={() => openPhotoModal(imageUrl, 'Input Image Preview')}>
                      <img
                        src={imageUrl}
                        alt="Input preview"
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-200"
                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YWFhYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="bg-white/90 dark:bg-black/90 rounded-full p-2">
                          <ImageIcon className="h-4 w-4 text-primary" />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Click to view full size</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="file" className="space-y-4">
                <FileUpload
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/tiff,image/tif"
                  maxSize={10}
                  onFileChangeAction={handleFileChange}
                  showPreview={true}
                  placeholder="Drop an image here or click to select"
                  label="Upload Image"
                />
              </TabsContent>

              <TabsContent value="samples" className="space-y-4">
                {isLoadingSamples ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2 text-muted-foreground">Loading sample images...</span>
                  </div>
                ) : sampleImages.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {sampleImages.map((imagePath, index) => (
                      <div
                        key={index}
                        className="group relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-primary transition-all duration-200 hover:shadow-lg"
                        onClick={() => handleSampleImageSelect(imagePath)}
                        onDoubleClick={() => openPhotoModal(imagePath, `Sample Image ${index + 1}`)}
                      >
                        <img
                          src={imagePath}
                          alt={`Sample ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          onError={(e) => {
                            // Hide broken images
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-white/90 dark:bg-black/90 rounded-full p-2">
                              <Wand2 className="h-4 w-4 text-primary" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No sample images available</p>
                    <p className="text-sm">Add images to the public/samples folder</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            <Button
              onClick={handleBackgroundRemoval}
              disabled={fromUrlMutation.isPending || fromFileMutation.isPending || (inputMethod === 'url' ? !imageUrl.trim() : !uploadedFile)}
              className="w-full btn-enhanced hover-lift"
            >
              {fromUrlMutation.isPending || fromFileMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Remove Background
                </>
              )}
            </Button>
          </CardContent>
        </Card>


        {/* Result Section */}
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Result
            </CardTitle>
            <CardDescription>
              Your image with background removed
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {fromUrlMutation.isPending || fromFileMutation.isPending ? (
              <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Removing background...
                  </p>
                </div>
              </div>
            ) : resultImageUrl ? (
              <div className="space-y-4">
                <div className="rounded-lg overflow-hidden border cursor-pointer group relative" onClick={() => openPhotoModal(resultImageUrl, 'Background Removed Result')}>
                  <img
                    src={resultImageUrl}
                    alt="Background removed"
                    className="w-full h-48 object-contain bg-muted group-hover:scale-105 transition-transform duration-200"
                    onLoad={() => console.log('Image loaded successfully:', resultImageUrl)}
                    onError={(e) => {
                      console.error('Image failed to load:', resultImageUrl, e);
                      // Try the original URL as fallback
                      if (originalImageUrl && resultImageUrl !== originalImageUrl) {
                        console.log('Trying original URL as fallback:', originalImageUrl);
                        setResultImageUrl(originalImageUrl);
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="bg-white/90 dark:bg-black/90 rounded-full p-2">
                      <ImageIcon className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Click to view full size</p>
                <DownloadComponent
                  data={resultImageUrl}
                  filename={generateFilename(originalFilename)}
                  onDownload={async (format: string, filename: string) => {
                    try {
                      const response = await fetch(resultImageUrl);
                      const blob = await response.blob();
                      
                      // Create download link
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = filename;
                      link.style.display = 'none';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                      
                      toast.success('Image downloaded successfully!');
                    } catch (error) {
                      console.error('Download failed:', error);
                      toast.error('Failed to download image');
                    }
                  }}
                  className="w-full"
                  allowedCategories={['image']}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Image
                </DownloadComponent>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
                <div className="text-center">
                  <ImageIcon className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Process an image to see the result
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="mt-8 card-enhanced">
        <CardHeader>
          <CardTitle>How to use</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
            <li>Choose between URL input or file upload</li>
            <li>Provide your image using the selected method</li>
            <li>Click "Remove Background" to start the AI processing</li>
            <li>Wait for the background removal to complete</li>
            <li>Download your processed image</li>
          </ol>
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> For URL input, the image must be publicly accessible. For file upload, supported formats are JPG, PNG, WebP, and TIFF (max 10MB).
            </p>
          </div>
          
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Try these example URLs:</strong>
            </p>
            <div className="space-y-1 text-xs">
              <button 
                onClick={() => setImageUrl('https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400')}
                className="text-primary hover:underline block"
              >
                • Portrait photo
              </button>
              <button 
                onClick={() => setImageUrl('https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400')}
                className="text-primary hover:underline block"
              >
                • Person with background
              </button>
              <button 
                onClick={() => setImageUrl('https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400')}
                className="text-primary hover:underline block"
              >
                • Product photo
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
        </div>
      </div>

      {/* Photo Modal */}
      <PhotoModal
        isOpen={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        imageUrl={photoModalImage}
        title={photoModalTitle}
        showDownload={photoModalImage === resultImageUrl}
        downloadFilename={photoModalImage === resultImageUrl ? generateFilename(originalFilename) : 'image'}
      />
    </div>
  );
}

export default function BackgroundRemovalPage() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 py-8">Loading...</div>}>
      <BackgroundRemovalPageContent />
    </Suspense>
  );
}
